import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import { AuthProvider } from "@/context/AuthContext";
import Header from "@/components/Header";
import ExpiringCouponBanner from "@/components/ExpiringCouponBanner";
import Footer from "@/components/Footer";
import WhatsAppFloatButton from "@/components/WhatsAppFloatButton";
import ChatWidget from "@/components/ChatWidget";
import ThemeLoader from "@/components/ThemeLoader";
import AnalyticsTracker from "@/components/AnalyticsTracker";
import ScrollToTop from "@/components/ScrollToTop";

import Home from "@/pages/Home";
import About from "@/pages/About";
import ServiceDetail from "@/pages/ServiceDetail";
import Packages from "@/pages/Packages";
import PackageDetail from "@/pages/PackageDetail";
import Vacancies from "@/pages/Vacancies";
import VacancyDetail from "@/pages/VacancyDetail";
import Visas from "@/pages/Visas";
import VisaDetail from "@/pages/VisaDetail";
import ApplicationSuccess from "@/pages/ApplicationSuccess";
import ReferEarn from "@/pages/ReferEarn";
import BecomeAgent from "@/pages/BecomeAgent";
import BecomePartner from "@/pages/BecomePartner";
import NotFound from "@/pages/NotFound";
import TrackApplication from "@/pages/TrackApplication";
import MyCoupons from "@/pages/MyCoupons";
import BlogList from "@/pages/BlogList";
import BlogDetail from "@/pages/BlogDetail";
import Contact from "@/pages/Contact";
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import Terms from "@/pages/legal/Terms";
import RefundPolicy from "@/pages/legal/RefundPolicy";
import ShippingPolicy from "@/pages/legal/ShippingPolicy";

// Admin panel is lazy-loaded as a separate bundle chunk - it's never needed by
// public storefront visitors, so keeping it out of the main bundle reduces
// the JS payload/parse time for every public page load (Core Web Vitals: TBT/LCP).
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminOverview = lazy(() => import("@/pages/admin/AdminOverview"));
const AdminApplications = lazy(() => import("@/pages/admin/AdminApplications"));
const AdminPayouts = lazy(() => import("@/pages/admin/AdminPayouts"));
const AdminWallets = lazy(() => import("@/pages/admin/AdminWallets"));
const AdminDocuments = lazy(() => import("@/pages/admin/AdminDocuments"));
const AdminMarriageCerts = lazy(() => import("@/pages/admin/AdminMarriageCerts"));
const MarriageCertPrint = lazy(() => import("@/pages/admin/MarriageCertPrint"));
const VerifyCertificate = lazy(() => import("@/pages/VerifyCertificate"));
const AdminServices = lazy(() => import("@/pages/admin/AdminServices"));
const AdminBanners = lazy(() => import("@/pages/admin/AdminBanners"));
const AdminBlog = lazy(() => import("@/pages/admin/AdminBlog"));
const AdminFAQs = lazy(() => import("@/pages/admin/AdminFAQs"));
const AdminLocations = lazy(() => import("@/pages/admin/AdminLocations"));
const AdminPartners = lazy(() => import("@/pages/admin/AdminPartners"));
const AdminSettings = lazy(() => import("@/pages/admin/AdminSettings"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminAccount = lazy(() => import("@/pages/admin/AdminAccount"));
const AdminChats = lazy(() => import("@/pages/admin/AdminChats"));
const AdminSEO = lazy(() => import("@/pages/admin/AdminSEO"));
const AdminAbout = lazy(() => import("@/pages/admin/AdminAbout"));
const AdminPackages = lazy(() => import("@/pages/admin/AdminPackages"));
const AdminVacancies = lazy(() => import("@/pages/admin/AdminVacancies"));
const AdminVisas = lazy(() => import("@/pages/admin/AdminVisas"));
const PartnerLogin = lazy(() => import("@/pages/partner/PartnerLogin"));
const PartnerRegister = lazy(() => import("@/pages/partner/PartnerRegister"));
const PartnerLayout = lazy(() => import("@/pages/partner/PartnerLayout"));
const PartnerApplications = lazy(() => import("@/pages/partner/PartnerApplications"));
const PartnerServices = lazy(() => import("@/pages/partner/PartnerServices"));
const PartnerServiceDetail = lazy(() => import("@/pages/partner/PartnerServiceDetail"));
const PartnerEarnings = lazy(() => import("@/pages/partner/PartnerEarnings"));
const PartnerNotifications = lazy(() => import("@/pages/partner/PartnerNotifications"));
const PartnerProfile = lazy(() => import("@/pages/partner/PartnerProfile"));
const PartnerManager = lazy(() => import("@/pages/partner/PartnerManager"));
const PartnerAdditionalPayments = lazy(() => import("@/pages/partner/PartnerAdditionalPayments"));
const AgentManager = lazy(() => import("@/pages/agent/AgentManager"));
const AgentLogin = lazy(() => import("@/pages/agent/AgentLogin"));
const AgentRegister = lazy(() => import("@/pages/agent/AgentRegister"));
const AgentLayout = lazy(() => import("@/pages/agent/AgentLayout"));
const AgentApplications = lazy(() => import("@/pages/agent/AgentApplications"));
const AgentOverview = lazy(() => import("@/pages/agent/AgentOverview"));
const AgentStatement = lazy(() => import("@/pages/agent/AgentStatement"));
const AgentTracking = lazy(() => import("@/pages/agent/AgentTracking"));
const AgentWallet = lazy(() => import("@/pages/agent/AgentWallet"));
const AgentReferrals = lazy(() => import("@/pages/agent/AgentReferrals"));
const AgentNewApplication = lazy(() => import("@/pages/agent/AgentNewApplication"));
const AgentServices = lazy(() => import("@/pages/agent/AgentServices"));
const AgentProfile = lazy(() => import("@/pages/agent/AgentProfile"));
const AgentChat = lazy(() => import("@/pages/agent/AgentChat"));
const PartnerChat = lazy(() => import("@/pages/partner/PartnerChat"));
const AdminChatCenter = lazy(() => import("@/pages/admin/AdminChatCenter"));
const AgentTrack = lazy(() => import("@/pages/AgentTrack"));
const AdminAgents = lazy(() => import("@/pages/admin/AdminAgents"));
const AdminManagers = lazy(() => import("@/pages/admin/AdminManagers"));
const AdminAdditionalPayments = lazy(() => import("@/pages/admin/AdminAdditionalPayments"));
const AdminRegistrationForms = lazy(() => import("@/pages/admin/AdminRegistrationForms"));
const AdminActivityLog = lazy(() => import("@/pages/admin/AdminActivityLog"));

const ADMIN_PATH = process.env.REACT_APP_ADMIN_PATH;

function AdminFallback() {
  return <div className="flex items-center justify-center min-h-screen"><Loader2 className="animate-spin text-[#b1976b]" size={28} /></div>;
}

function SiteLayout({ children }) {
  return (
    <>
      <ThemeLoader />
      <Header />
      <ExpiringCouponBanner />
      {children}
      <Footer />
      <WhatsAppFloatButton />
      <ChatWidget />
    </>
  );
}

function App() {
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) localStorage.setItem("gu_ref_agent", ref.trim().toUpperCase());
    const cref = new URLSearchParams(window.location.search).get("cref");
    if (cref) localStorage.setItem("gu_ref_customer", cref.trim().toUpperCase());
  }, []);
  return (
    <AuthProvider>
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <ScrollToTop />
        <AnalyticsTracker />
        <Routes>
          <Route path="/" element={<SiteLayout><Home /></SiteLayout>} />
          <Route path="/about" element={<SiteLayout><About /></SiteLayout>} />
          <Route path="/services/:slug" element={<SiteLayout><ServiceDetail /></SiteLayout>} />
          <Route path="/packages" element={<SiteLayout><Packages /></SiteLayout>} />
          <Route path="/packages/:slug" element={<SiteLayout><PackageDetail /></SiteLayout>} />
          <Route path="/vacancies" element={<SiteLayout><Vacancies /></SiteLayout>} />
          <Route path="/vacancies/:slug" element={<SiteLayout><VacancyDetail /></SiteLayout>} />
          <Route path="/visas" element={<SiteLayout><Visas /></SiteLayout>} />
          <Route path="/visas/:slug" element={<SiteLayout><VisaDetail /></SiteLayout>} />
          <Route path="/application-success" element={<SiteLayout><ApplicationSuccess /></SiteLayout>} />
          <Route path="/track" element={<SiteLayout><TrackApplication /></SiteLayout>} />
          <Route path="/verify-certificate" element={<SiteLayout><VerifyCertificate /></SiteLayout>} />
          <Route path="/my-coupons" element={<SiteLayout><MyCoupons /></SiteLayout>} />
          <Route path="/refer-and-earn" element={<SiteLayout><ReferEarn /></SiteLayout>} />
          <Route path="/become-agent" element={<SiteLayout><BecomeAgent /></SiteLayout>} />
          <Route path="/become-partner" element={<SiteLayout><BecomePartner /></SiteLayout>} />
          <Route path="/blog" element={<SiteLayout><BlogList /></SiteLayout>} />
          <Route path="/blog/:slug" element={<SiteLayout><BlogDetail /></SiteLayout>} />
          <Route path="/contact" element={<SiteLayout><Contact /></SiteLayout>} />
          <Route path="/privacy-policy" element={<SiteLayout><PrivacyPolicy /></SiteLayout>} />
          <Route path="/terms-and-conditions" element={<SiteLayout><Terms /></SiteLayout>} />
          <Route path="/cancellation-refund-policy" element={<SiteLayout><RefundPolicy /></SiteLayout>} />
          <Route path="/shipping-delivery-policy" element={<SiteLayout><ShippingPolicy /></SiteLayout>} />

          <Route path="/partner/login" element={<Suspense fallback={<AdminFallback />}><PartnerLogin /></Suspense>} />
          <Route path="/agent-track/:agentCode" element={<Suspense fallback={<AdminFallback />}><AgentTrack /></Suspense>} />
          <Route path="/agent/login" element={<Suspense fallback={<AdminFallback />}><AgentLogin /></Suspense>} />
          <Route path="/agent/register" element={<Suspense fallback={<AdminFallback />}><AgentRegister /></Suspense>} />
          <Route path="/agent/dashboard" element={<Suspense fallback={<AdminFallback />}><AgentLayout /></Suspense>}>
            <Route index element={<Suspense fallback={<AdminFallback />}><AgentOverview /></Suspense>} />
            <Route path="applications" element={<Suspense fallback={<AdminFallback />}><AgentApplications /></Suspense>} />
            <Route path="statement" element={<Suspense fallback={<AdminFallback />}><AgentStatement /></Suspense>} />
            <Route path="tracking" element={<Suspense fallback={<AdminFallback />}><AgentTracking /></Suspense>} />
            <Route path="wallet" element={<Suspense fallback={<AdminFallback />}><AgentWallet /></Suspense>} />
            <Route path="referrals" element={<Suspense fallback={<AdminFallback />}><AgentReferrals /></Suspense>} />
            <Route path="new" element={<Suspense fallback={<AdminFallback />}><AgentNewApplication /></Suspense>} />
            <Route path="services" element={<Suspense fallback={<AdminFallback />}><AgentServices /></Suspense>} />
            <Route path="track" element={<Suspense fallback={<AdminFallback />}><TrackApplication /></Suspense>} />
            <Route path="chat" element={<Suspense fallback={<AdminFallback />}><AgentChat /></Suspense>} />
            <Route path="manager" element={<Suspense fallback={<AdminFallback />}><AgentManager /></Suspense>} />
            <Route path="profile" element={<Suspense fallback={<AdminFallback />}><AgentProfile /></Suspense>} />
          </Route>
          <Route path="/partner/register" element={<Suspense fallback={<AdminFallback />}><PartnerRegister /></Suspense>} />
          <Route path="/partner/dashboard" element={<Suspense fallback={<AdminFallback />}><PartnerLayout /></Suspense>}>
            <Route index element={<Suspense fallback={<AdminFallback />}><PartnerApplications /></Suspense>} />
            <Route path="services" element={<Suspense fallback={<AdminFallback />}><PartnerServices /></Suspense>} />
            <Route path="chat" element={<Suspense fallback={<AdminFallback />}><PartnerChat /></Suspense>} />
            <Route path="services/:id" element={<Suspense fallback={<AdminFallback />}><PartnerServiceDetail /></Suspense>} />
            <Route path="earnings" element={<Suspense fallback={<AdminFallback />}><PartnerEarnings /></Suspense>} />
            <Route path="notifications" element={<Suspense fallback={<AdminFallback />}><PartnerNotifications /></Suspense>} />
            <Route path="manager" element={<Suspense fallback={<AdminFallback />}><PartnerManager /></Suspense>} />
            <Route path="payment-requests" element={<Suspense fallback={<AdminFallback />}><PartnerAdditionalPayments /></Suspense>} />
            <Route path="profile" element={<Suspense fallback={<AdminFallback />}><PartnerProfile /></Suspense>} />
          </Route>

          <Route path={`/${ADMIN_PATH}/login`} element={<Suspense fallback={<AdminFallback />}><AdminLogin /></Suspense>} />
          <Route path={`/${ADMIN_PATH}/dashboard`} element={<Suspense fallback={<AdminFallback />}><AdminLayout /></Suspense>}>
            <Route index element={<Suspense fallback={<AdminFallback />}><AdminOverview /></Suspense>} />
            <Route path="applications" element={<Suspense fallback={<AdminFallback />}><AdminApplications /></Suspense>} />
            <Route path="managers" element={<Suspense fallback={<AdminFallback />}><AdminManagers /></Suspense>} />
            <Route path="additional-payments" element={<Suspense fallback={<AdminFallback />}><AdminAdditionalPayments /></Suspense>} />
            <Route path="payouts" element={<Suspense fallback={<AdminFallback />}><AdminPayouts /></Suspense>} />
            <Route path="wallets" element={<Suspense fallback={<AdminFallback />}><AdminWallets /></Suspense>} />
            <Route path="registration-forms" element={<Suspense fallback={<AdminFallback />}><AdminRegistrationForms /></Suspense>} />
            <Route path="documents" element={<Suspense fallback={<AdminFallback />}><AdminDocuments /></Suspense>} />
            <Route path="marriage-certs" element={<Suspense fallback={<AdminFallback />}><AdminMarriageCerts /></Suspense>} />
            <Route path="marriage-certs/print/:certId" element={<Suspense fallback={<AdminFallback />}><MarriageCertPrint /></Suspense>} />
            <Route path="services" element={<Suspense fallback={<AdminFallback />}><AdminServices /></Suspense>} />
            <Route path="packages" element={<Suspense fallback={<AdminFallback />}><AdminPackages /></Suspense>} />
            <Route path="vacancies" element={<Suspense fallback={<AdminFallback />}><AdminVacancies /></Suspense>} />
            <Route path="visas" element={<Suspense fallback={<AdminFallback />}><AdminVisas /></Suspense>} />
            <Route path="banners" element={<Suspense fallback={<AdminFallback />}><AdminBanners /></Suspense>} />
            <Route path="blog" element={<Suspense fallback={<AdminFallback />}><AdminBlog /></Suspense>} />
            <Route path="faqs" element={<Suspense fallback={<AdminFallback />}><AdminFAQs /></Suspense>} />
            <Route path="locations" element={<Suspense fallback={<AdminFallback />}><AdminLocations /></Suspense>} />
            <Route path="partners" element={<Suspense fallback={<AdminFallback />}><AdminPartners /></Suspense>} />
            <Route path="agents" element={<Suspense fallback={<AdminFallback />}><AdminAgents /></Suspense>} />
            <Route path="chat-center" element={<Suspense fallback={<AdminFallback />}><AdminChatCenter /></Suspense>} />
            <Route path="settings" element={<Suspense fallback={<AdminFallback />}><AdminSettings /></Suspense>} />
            <Route path="admin-users" element={<Suspense fallback={<AdminFallback />}><AdminUsers /></Suspense>} />
            <Route path="activity-log" element={<Suspense fallback={<AdminFallback />}><AdminActivityLog /></Suspense>} />
            <Route path="account" element={<Suspense fallback={<AdminFallback />}><AdminAccount /></Suspense>} />
            <Route path="chats" element={<Suspense fallback={<AdminFallback />}><AdminChats /></Suspense>} />
            <Route path="seo" element={<Suspense fallback={<AdminFallback />}><AdminSEO /></Suspense>} />
            <Route path="about" element={<Suspense fallback={<AdminFallback />}><AdminAbout /></Suspense>} />
          </Route>

          <Route path="*" element={<SiteLayout><NotFound /></SiteLayout>} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;

